import { useRef, useState } from 'react'
import * as api from 'api/entityApi'
import { useToast } from 'components/common/Toast'

const AVATAR_FALLBACK =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 90 90"><rect width="90" height="90" fill="#e5f6ec"/><circle cx="45" cy="34" r="16" fill="#bfd4c8"/><path d="M12 88c4-19 17-28 33-28s29 9 33 28z" fill="#bfd4c8"/></svg>`,
  )

/**
 * Port of `ManageEntityProfileImage`. The pencil opens the file picker and
 * uploads through `Common/UploadAttachment`; the cross clears the preview back
 * to the placeholder, which is exactly what the legacy 'remove' branch did (it
 * reset the buttons without deleting anything server-side).
 */
export function ProfileImageUploader({
  entityType,
  entityId,
  initialUrl,
  disabled,
}: {
  entityType: string
  entityId: string
  initialUrl?: string
  disabled?: boolean
}) {
  const toast = useToast()
  const inputRef = useRef<HTMLInputElement>(null)
  const [url, setUrl] = useState(initialUrl ?? '')
  const [uploading, setUploading] = useState(false)

  const handleFile = async (file: File | undefined) => {
    if (!file) return
    setUploading(true)
    try {
      const uploadedUrl = await api.uploadProfileImage({ entityType, entityId, file })
      setUrl(uploadedUrl)
      toast.push('success', 'Profile image updated.')
    } catch (err) {
      toast.push('error', (err as Error).message)
    } finally {
      setUploading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  return (
    <div className="profile-uploader">
      <div className="user-img scorebox">
        <img
          src={url || AVATAR_FALLBACK}
          alt="Profile"
          onError={(e) => {
            e.currentTarget.src = AVATAR_FALLBACK
          }}
        />
        <span className="scorecard profile-actions">
          {url ? (
            <button type="button" title="Remove image" disabled={uploading} onClick={() => setUrl('')}>
              ✕
            </button>
          ) : (
            <button
              type="button"
              title="Upload image"
              disabled={uploading || disabled}
              onClick={() => inputRef.current?.click()}
            >
              ✎
            </button>
          )}
        </span>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/gif"
        onChange={(e) => void handleFile(e.target.files?.[0])}
      />

      <span className="font12 text-muted">
        {uploading ? 'Uploading…' : 'PNG, JPG or GIF · under 1 MB'}
      </span>
    </div>
  )
}
